import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2, GraduationCap, Store, Users, UserRound } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "school_coordinator", label: "School / Institution Coordinator", icon: GraduationCap, desc: "Register your institution and request events or trips." },
  { value: "provider", label: "Provider / Facility / Coach", icon: Store, desc: "List services and receive approved booking work." },
  { value: "parent", label: "Parent / Guardian", icon: Users, desc: "Track participation and give consent." },
  { value: "individual", label: "Individual Participant", icon: UserRound, desc: "Join matches, book grounds and track your own game." },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "school_coordinator" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const dest = form.role === "individual" ? "/app/onboarding" : "/app";
    // Pre-set the redirect param so the <Public> wrapper routes here once auth state flips.
    if (form.role === "individual") setSearchParams({ redirect: dest }, { replace: true });
    try {
      await register({ name: form.name, email: form.email, password: form.password, phone: form.phone, roles: [form.role] });
      toast.success("Account created");
      navigate(dest);
    } catch (err) {
      const msg = apiError(err);
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-lg animate-fadeUp">
        <Link to="/" className="mb-8 flex flex-col items-center gap-2">
          <img src="/spora1-logo.webp" alt="SPORA1" className="h-12 w-auto" />
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
            One Ecosystem <span className="text-primary">•</span> One Technology <span className="text-primary">•</span> Many Champions
          </p>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-2xl">
          <h1 className="font-display text-3xl font-bold">Create your account</h1>
          <p className="mt-1 text-sm text-muted-foreground">Join SPORA1 to manage sports events & tourism</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium">I am a…</label>
              <div className="grid gap-2">
                {ROLE_OPTIONS.map((r) => {
                  const Icon = r.icon;
                  const active = form.role === r.value;
                  return (
                    <button type="button" key={r.value} data-testid={`role-${r.value}`}
                      onClick={() => setForm((f) => ({ ...f, role: r.value }))}
                      className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-all ${
                        active ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/40"}`}>
                      <Icon className={`mt-0.5 h-5 w-5 ${active ? "text-primary" : "text-muted-foreground"}`} />
                      <div>
                        <p className="text-sm font-semibold">{r.label}</p>
                        <p className="text-xs text-muted-foreground">{r.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium">Full name</label>
                <input data-testid="register-name-input" required value={form.name} onChange={set("name")}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Phone</label>
                <input data-testid="register-phone-input" value={form.phone} onChange={set("phone")}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Email</label>
              <input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Password</label>
              <input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")}
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" placeholder="Min 6 characters" />
            </div>
            {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
            <button data-testid="register-submit-btn" type="submit" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 font-semibold text-primary-foreground transition-all hover:bg-[#FF8533] disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create account
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="font-medium text-primary hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
